import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Link, useNavigate } from "react-router-dom"
import {
	AdminClient,
	type OntologyPack,
	type OntologyPreset,
	type ServiceSummary
} from "../api/admin-client.ts"
import type { AuthContext } from "../components/AdminAuthGate.tsx"
import { uniqueBlockIds } from "../components/ontology/pack-block-utils.ts"
import { Skeleton, SkeletonText } from "../components/Skeleton.tsx"
import { useAsyncData } from "../hooks/useAsyncData.ts"
import { useInfiniteScroll } from "../hooks/useInfiniteScroll.ts"

type StatusFilter = "all" | "active" | "suspended"

/** 카드 정렬 키 — toolbar pill segmented 로 선택. */
type SortKey = "name" | "status" | "recent"

/** 뷰 모드 — 카드 그리드 vs 테이블. BlocksPage 의 ads-segment 와 동일 패턴. */
type ViewMode = "grid" | "table"

/** 카드 상단 팩 칩 최대 표시 개수. 넘치면 "+N" 뱃지로 overflow 처리. */
const MAX_PACK_CHIPS = 4

/**
 * 서비스별 KG 노드/엣지 개수 상태.
 * - `"loading"` — 서버 호출 진행 중
 * - `"error"` — 실패 (Neptune OOM 503 포함). UI 는 `—` 로 조용히 렌더
 * - `{ nodes, edges }` — 성공
 */
type UsageState = "loading" | "error" | { readonly nodes: number; readonly edges: number }

function sumValues(r: Record<string, number>): number {
	let total = 0
	for (const v of Object.values(r)) total += v
	return total
}

/** 1.2k / 34k / 1.2M 형태로 큰 수 축약. 1000 미만은 그대로. */
function formatCount(n: number): string {
	if (n < 1000) return String(n)
	if (n < 10_000) return `${(n / 1000).toFixed(1)}k`
	if (n < 1_000_000) return `${Math.round(n / 1000)}k`
	return `${(n / 1_000_000).toFixed(1)}M`
}

/**
 * 서비스 목록의 각 서비스별 노드/엣지 규모를 부분 업데이트로 병렬 fetch.
 * 목록 응답은 즉시 렌더하고, 규모는 들어오는 대로 row/카드에 반영된다.
 * 개별 실패는 silent — "error" 로 마크 후 `—` 표시.
 *
 * 호출부가 화면에 렌더되는 paged(visible) 서비스만 전달하므로 동시 호출이 pageSize 로 자연 제한된다.
 * 무한스크롤로 페이지가 늘 때는 신규 slug 만 추가 fetch — 이미 받아온 규모는 ref 로 추적해 재요청하지 않는다.
 */
function useServiceUsageMap(
	client: AdminClient,
	services: ReadonlyArray<ServiceSummary>
): ReadonlyMap<string, UsageState> {
	const slugsKey = services.map((s) => s.serviceSlug).join("|")
	const [map, setMap] = useState<ReadonlyMap<string, UsageState>>(new Map())
	// client 가 바뀌면(세션 재생성) 요청 추적을 초기화하기 위한 ref.
	const requestedRef = useRef<{ readonly client: AdminClient; readonly slugs: Set<string> }>({
		client,
		slugs: new Set()
	})
	// biome-ignore lint/correctness/useExhaustiveDependencies: slugsKey 로 services 배열 identity 변경을 좁게 감지
	useEffect(() => {
		let cancelled = false
		// client 변경 시 추적 초기화.
		if (requestedRef.current.client !== client) {
			requestedRef.current = { client, slugs: new Set() }
			setMap(new Map())
		}
		const requested = requestedRef.current.slugs
		// 아직 요청하지 않은 신규 slug 만 추려 fetch — 기존 페이지는 재요청하지 않는다.
		const pending = services.map((s) => s.serviceSlug).filter((slug) => !requested.has(slug))
		if (pending.length === 0) return
		setMap((prev) => {
			const next = new Map(prev)
			for (const slug of pending) next.set(slug, "loading")
			return next
		})
		for (const slug of pending) {
			requested.add(slug)
			client.getOntologyUsage(slug).then(
				(u) => {
					if (cancelled) return
					setMap((prev) => {
						const next = new Map(prev)
						next.set(slug, { nodes: sumValues(u.kinds), edges: sumValues(u.relations) })
						return next
					})
				},
				() => {
					if (cancelled) return
					setMap((prev) => {
						const next = new Map(prev)
						next.set(slug, "error")
						return next
					})
				}
			)
		}
		return () => {
			cancelled = true
		}
	}, [client, slugsKey])
	return map
}

export function ServicesPage(props: { readonly auth: AuthContext }) {
	const [packs, setPacks] = useState<ReadonlyArray<OntologyPack>>([])
	const [presets, setPresets] = useState<ReadonlyArray<OntologyPreset>>([])
	const [wizardOpen, setWizardOpen] = useState(false)
	const [query, setQuery] = useState("")
	const [status, setStatus] = useState<StatusFilter>("all")
	const [sort, setSort] = useState<SortKey>("name")
	// 기본 뷰는 테이블 — 다수 서비스가 있을 때 스캔이 유리해서 피드백으로 기본값 전환.
	const [view, setView] = useState<ViewMode>("table")

	const client = useMemo(
		() => new AdminClient(props.auth.baseUrl, props.auth.invalidateSession),
		[props.auth.baseUrl, props.auth.invalidateSession]
	)

	// 서비스 목록 로드. refresh() 로 등록 위저드 완료 후 재호출.
	const {
		data: servicesData,
		error,
		refresh
	} = useAsyncData<ReadonlyArray<ServiceSummary>>(() => client.listServices(), [client])
	const services = servicesData ?? []
	// 스켈레톤은 "최초 로드(아직 데이터 없음)" 에만 노출 — refresh() 재호출 시엔 기존 목록을 유지(기존 동등성).
	// 에러 시엔 스켈레톤 대신 에러 배너가 떠야 하므로 error 가 있으면 false.
	const loading = !error && servicesData === undefined

	// 팩/프리셋 카탈로그 — 서비스 카드 팩 칩 렌더 + 스타터 매치 라벨용.
	// best-effort — 실패해도 카드는 id 만으로 렌더된다.
	useEffect(() => {
		let cancelled = false
		void (async () => {
			try {
				const [packList, presetList] = await Promise.all([
					client.getOntologyPacks(),
					client.getOntologyPresets()
				])
				if (!cancelled) {
					setPacks(packList)
					setPresets(presetList)
				}
			} catch {
				// 무시 — 카드는 fallback 으로 id 칩만 표시
			}
		})()
		return () => {
			cancelled = true
		}
	}, [client])

	const activeCount = services.filter((s) => s.status === "active").length
	const suspendedCount = services.length - activeCount

	// 팩 id → 팩 메타 맵. 카드에서 아이콘/이름을 조회해 칩 렌더에 사용.
	const packById = useMemo(() => new Map(packs.map((p) => [p.id, p])), [packs])

	/**
	 * 슬라이스의 packIds 가 어떤 스타터 preset 과 정확히 일치하면 그 preset 을 반환. 없으면 undefined.
	 * 서비스 카드의 "Matches starter" 라벨 판정.
	 */
	const matchedPresetFor = useMemo(() => {
		return (service: ServiceSummary): OntologyPreset | undefined => {
			const ids = service.ontologySlice.packIds ?? []
			if (ids.length === 0) return undefined
			const set = new Set(ids)
			return presets.find((p) => {
				if (p.packIds.length !== ids.length) return false
				for (const id of p.packIds) if (!set.has(id)) return false
				return true
			})
		}
	}, [presets])

	/** 서비스 하나의 고유 블럭 수 — 카드의 "N blocks" 뱃지. packs 카탈로그가 늦게 오면 0. */
	const blockCountFor = useCallback(
		(service: ServiceSummary): number => {
			const ids = service.ontologySlice.packIds ?? []
			if (ids.length === 0 || packs.length === 0) return 0
			const selected = ids.flatMap((id) => {
				const p = packById.get(id)
				return p ? [p] : []
			})
			return uniqueBlockIds(selected).length
		},
		[packs, packById]
	)

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase()
		const matched = services.filter((s) => {
			if (status !== "all" && s.status !== status) return false
			if (!q) return true
			return (
				s.serviceSlug.toLowerCase().includes(q) ||
				s.displayName.toLowerCase().includes(q) ||
				s.ownerEmail.toLowerCase().includes(q)
			)
		})
		// 정렬 — name/status/recent
		const sorted = [...matched]
		if (sort === "name") {
			sorted.sort((a, b) => a.displayName.localeCompare(b.displayName))
		} else if (sort === "status") {
			// active 먼저 → 이름순
			sorted.sort((a, b) => {
				if (a.status !== b.status) return a.status === "active" ? -1 : 1
				return a.displayName.localeCompare(b.displayName)
			})
		} else {
			// recent — 최신 생성이 먼저 (createdAt 내림차순)
			sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
		}
		return sorted
	}, [services, query, status, sort])

	const filterActive = query.trim().length > 0 || status !== "all"
	const {
		visible: paged,
		sentinelRef,
		hasMore,
		loadedCount,
		totalCount
	} = useInfiniteScroll(filtered, {
		pageSize: 30
	})

	// 서비스별 KG 규모 (노드/엣지) — 화면에 실제 렌더되는 paged(visible) 서비스만 대상으로 비동기 fetch.
	// services 전체가 아니라 paged 로 좁혀 동시 호출이 pageSize(30) 로 자연 제한된다.
	// slugsKey 가 동일 slug 집합이면 재발사하지 않으므로 무한스크롤로 페이지가 늘 때만 신규 slug 만 추가 fetch 된다.
	const usageMap = useServiceUsageMap(client, paged)
	const usageFor = useCallback(
		(s: ServiceSummary): UsageState | undefined => usageMap.get(s.serviceSlug),
		[usageMap]
	)

	return (
		<div className="content">
			<div className="page-head">
				<div>
					<h2>서비스</h2>
					<div className="sub">MCP 엔드포인트가 노출된 멀티테넌트 서비스 목록입니다.</div>
				</div>
				<div className="services-page-head-actions">
					<Link to="/blocks" className="btn btn-ghost btn-sm">
						<svg
							className="icon"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
							focusable="false"
						>
							<title>블럭 둘러보기</title>
							<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
							<path d="M3.27 6.96 12 12.01l8.73-5.05" />
							<path d="M12 22.08V12" />
						</svg>
						Browse blocks
					</Link>
					<button type="button" className="btn btn-primary" onClick={() => setWizardOpen(true)}>
						<svg
							className="icon"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
							focusable="false"
						>
							<title>추가</title>
							<path d="M12 5v14M5 12h14" />
						</svg>
						서비스 등록
					</button>
				</div>
			</div>

			{error && (
				<div className="alert alert-danger" style={{ marginBottom: 18 }}>
					{error}
				</div>
			)}

			<div className="stats">
				<div className="stat">
					<div className="stat-v">
						{loading ? <Skeleton width={48} height={22} radius={6} inline /> : services.length}
					</div>
					<div className="stat-k">총 서비스 수</div>
				</div>
				<div className="stat">
					<div className="stat-v">
						{loading ? <Skeleton width={48} height={22} radius={6} inline /> : activeCount}
					</div>
					<div className="stat-k">활성</div>
				</div>
				<div className="stat">
					<div className="stat-v">
						{loading ? <Skeleton width={48} height={22} radius={6} inline /> : suspendedCount}
					</div>
					<div className="stat-k">정지</div>
				</div>
			</div>

			{!loading && services.length === 0 ? (
				<div className="empty">
					<div className="empty-icon">
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.8"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
							focusable="false"
						>
							<title>서비스</title>
							<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
							<path d="M3.27 6.96 12 12.01l8.73-5.05" />
							<path d="M12 22.08V12" />
						</svg>
					</div>
					<h3>첫 서비스를 등록하세요</h3>
					<p>
						서비스 등록이 완료되면 <code className="inline">/mcp/&#123;slug&#125;</code> 전용 MCP 엔드포인트가
						즉시 활성화됩니다.
					</p>
					<ul className="services-empty-steps">
						<li>
							<strong>1. 서비스 등록</strong> — slug · 이름 · 오너를 입력하면 서비스가 생성됩니다.
						</li>
						<li>
							<strong>2. 온톨로지 슬라이스 구성</strong> — 팩을 조합해 이 서비스의 지식 범위를 정합니다.
						</li>
						<li>
							<strong>3. MCP 엔드포인트 사용</strong> — 슬러그별 전용 엔드포인트로 LLM 이 질의합니다.
						</li>
					</ul>
					<div className="services-empty-actions">
						<button type="button" className="btn btn-primary" onClick={() => setWizardOpen(true)}>
							서비스 등록 시작
						</button>
						<Link to="/blocks" className="btn btn-ghost">
							블럭/팩 둘러보기
						</Link>
					</div>
				</div>
			) : (
				<div className="panel panel-table">
					<div className="table-toolbar">
						<label className="table-search">
							<svg
								className="icon"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<title>검색</title>
								<circle cx="11" cy="11" r="7" />
								<path d="m21 21-4.3-4.3" />
							</svg>
							<input
								type="search"
								placeholder="slug · 이름 · 오너 검색"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								spellCheck={false}
							/>
						</label>
						<div className="filter-group" aria-label="상태 필터">
							{(["all", "active", "suspended"] as const).map((s) => (
								<button
									key={s}
									type="button"
									aria-pressed={status === s}
									className={`filter-pill${status === s ? " is-active" : ""}`}
									onClick={() => setStatus(s)}
								>
									{s === "all" ? "전체" : s === "active" ? "활성" : "중지"}
									<span className="filter-count">
										{s === "all" ? services.length : s === "active" ? activeCount : suspendedCount}
									</span>
								</button>
							))}
						</div>
						<div className="filter-group" aria-label="정렬">
							{(
								[
									{ key: "name", label: "이름" },
									{ key: "status", label: "상태" },
									{ key: "recent", label: "최근" }
								] as const
							).map((o) => (
								<button
									key={o.key}
									type="button"
									aria-pressed={sort === o.key}
									className={`filter-pill${sort === o.key ? " is-active" : ""}`}
									onClick={() => setSort(o.key)}
								>
									{o.label}
								</button>
							))}
						</div>
						<div className="ads-segment services-page-view" role="tablist" aria-label="뷰 모드">
							{(
								[
									{ key: "grid", label: "카드", icon: "▦" },
									{ key: "table", label: "테이블", icon: "☰" }
								] as const
							).map((v) => (
								<button
									key={v.key}
									type="button"
									role="tab"
									aria-selected={view === v.key}
									className="ads-segment__item"
									onClick={() => setView(v.key)}
								>
									<span aria-hidden>{v.icon}</span>
									{v.label}
								</button>
							))}
						</div>
						<div className="table-count muted">
							{filterActive
								? `${filtered.length.toLocaleString()} / ${services.length.toLocaleString()}`
								: `${services.length.toLocaleString()} 개`}
						</div>
					</div>
					{loading ? (
						<ul className="services-page-grid">
							{Array.from({ length: 6 }, (_, i) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: deterministic skeleton
								<li key={i}>
									<ServiceCardSkeleton />
								</li>
							))}
						</ul>
					) : filtered.length === 0 ? (
						<div className="table-empty">
							<p>조건에 맞는 서비스가 없습니다.</p>
							{filterActive && (
								<button
									type="button"
									className="btn btn-sm btn-ghost"
									onClick={() => {
										setQuery("")
										setStatus("all")
									}}
								>
									필터 초기화
								</button>
							)}
						</div>
					) : view === "grid" ? (
						<>
							<ul className="services-page-grid">
								{paged.map((s) => (
									<li key={s.serviceSlug}>
										<ServiceCard
											service={s}
											packById={packById}
											blockCount={blockCountFor(s)}
											matchedPreset={matchedPresetFor(s)}
											baseUrl={props.auth.baseUrl}
											usage={usageFor(s)}
										/>
									</li>
								))}
							</ul>
							{totalCount > 0 && (
								<div ref={sentinelRef} className="infinite-scroll-sentinel" role="status" aria-live="polite">
									{hasMore ? `불러오는 중… (${loadedCount} / ${totalCount})` : `전체 ${totalCount}개 표시됨`}
								</div>
							)}
						</>
					) : (
						<>
							<ServiceTableView
								services={paged}
								packById={packById}
								blockCountFor={blockCountFor}
								matchedPresetFor={matchedPresetFor}
								baseUrl={props.auth.baseUrl}
								usageFor={usageFor}
							/>
							{totalCount > 0 && (
								<div ref={sentinelRef} className="infinite-scroll-sentinel" role="status" aria-live="polite">
									{hasMore ? `불러오는 중… (${loadedCount} / ${totalCount})` : `전체 ${totalCount}개 표시됨`}
								</div>
							)}
						</>
					)}
				</div>
			)}

			{wizardOpen && (
				<RegistrationWizard
					baseUrl={props.auth.baseUrl}
					onClose={() => setWizardOpen(false)}
					onRegistered={async () => {
						refresh()
					}}
					register={async (input) => {
						await client.registerService(input)
					}}
				/>
			)}
		</div>
	)
}

/** 서비스 카드 스켈레톤 — 실제 ServiceCard 높이에 맞춘 라인 배치. */
function ServiceCardSkeleton() {
	return (
		<div className="skel-card" aria-hidden>
			<div className="skel-card__head">
				<div className="skel-card__title-group">
					<Skeleton width="55%" height={15} />
					<Skeleton width="35%" height={11} />
				</div>
				<Skeleton width={60} height={20} radius={999} />
			</div>
			<SkeletonText lines={2} lineHeight={11} />
			<div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
				<Skeleton width={80} height={20} radius={999} />
				<Skeleton width={100} height={20} radius={999} />
				<Skeleton width={70} height={20} radius={999} />
			</div>
			<div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
				<Skeleton width={140} height={10} />
				<Skeleton width={90} height={10} />
			</div>
		</div>
	)
}

/**
 * 서비스 카드 — 목록의 개별 서비스를 이름/상태/팩 칩/블럭 수/스타터 매치 라벨로 요약.
 *
 * 내부 구성:
 * - 상단: 서비스 이름(+ slug) + 블럭 뱃지 + 상태 뱃지
 * - 팩 칩 리스트 — 최대 4개 노출, 초과분은 "+N" overflow 칩으로 축약
 * - 스타터 매치 라벨 (slice.presetId 가 있거나 packIds 셋이 preset 과 정확히 일치하면)
 * - 하단: 오너 이메일 · 생성일
 *
 * 카드 전체가 `/services/:slug` 로 이동하는 링크 역할을 하도록 최상위 요소를 Link 로 감싼다.
 */
function ServiceCard(props: {
	readonly service: ServiceSummary
	readonly packById: ReadonlyMap<string, OntologyPack>
	readonly blockCount: number
	readonly matchedPreset: OntologyPreset | undefined
	readonly baseUrl: string
	readonly usage: UsageState | undefined
}) {
	const { service, packById, blockCount, matchedPreset, baseUrl, usage } = props
	const packIds = service.ontologySlice.packIds ?? []
	const visibleIds = packIds.slice(0, MAX_PACK_CHIPS)
	const overflow = packIds.length - visibleIds.length
	// slice.presetId 가 있으면 우선, 없으면 packIds 셋 일치 preset 을 fallback
	const starterLabel = service.ontologySlice.presetId
		? (matchedPreset?.displayName ?? service.ontologySlice.presetId)
		: matchedPreset?.displayName

	return (
		<Link to={`/services/${service.serviceSlug}`} className="service-card">
			<div className="service-card-head">
				<div className="service-card-titles">
					<div className="service-card-title-row">
						<strong>{service.displayName}</strong>
						{blockCount > 0 ? (
							<span className="service-card-block-count" title={`${blockCount}개의 블럭으로 구성된 슬라이스`}>
								{blockCount} blocks
							</span>
						) : (
							<span
								className="service-card-slice-warn"
								title="아직 팩/블럭이 구성되지 않았습니다 — 상세 페이지에서 슬라이스를 설정하세요"
							>
								슬라이스 미설정
							</span>
						)}
					</div>
					<code className="inline service-card-slug">{service.serviceSlug}</code>
				</div>
				<span className={service.status === "active" ? "badge badge-active" : "badge badge-disabled"}>
					{service.status}
				</span>
			</div>

			<McpEndpointRow baseUrl={baseUrl} slug={service.serviceSlug} />

			{starterLabel && (
				<span className="badge service-card-starter" title="스타터 프리셋과 일치">
					스타터 일치 · {starterLabel}
				</span>
			)}

			{packIds.length > 0 && (
				<div className="service-card-packs" aria-label="팩 조합">
					{visibleIds.map((id) => (
						<PackChip key={id} id={id} pack={packById.get(id)} />
					))}
					{overflow > 0 && (
						<PackOverflowChip count={overflow} ids={packIds.slice(MAX_PACK_CHIPS)} packById={packById} />
					)}
				</div>
			)}

			<div className="service-card-foot">
				<span className="muted" title={service.ownerEmail}>
					{service.ownerEmail}
				</span>
				<UsageCell usage={usage} />
				<span className="muted">{service.createdAt.slice(0, 10)}</span>
			</div>
		</Link>
	)
}

/**
 * 서비스별 KG 규모 (노드/엣지) 셀 — 카드 foot / 테이블 셀 공용.
 * 노드는 filled dot (brand), 엣지는 선 (accent) 아이콘으로 타입을 구분한다.
 * 로딩 중에는 `…`, 실패(Neptune OOM 포함) 시 `—` 로 조용히 렌더.
 */
function UsageCell(props: { readonly usage: UsageState | undefined }) {
	const u = props.usage
	if (!u || u === "loading") {
		return (
			<span className="service-usage service-usage-quiet" aria-label="규모 로딩 중">
				…
			</span>
		)
	}
	if (u === "error") {
		return (
			<span className="service-usage service-usage-quiet" title="규모 집계 실패">
				—
			</span>
		)
	}
	return (
		<span
			className="service-usage"
			title={`노드 ${u.nodes.toLocaleString("ko-KR")} · 엣지 ${u.edges.toLocaleString("ko-KR")}`}
		>
			<span className="service-usage-metric service-usage-nodes" aria-label={`노드 ${u.nodes}`}>
				<span className="service-usage-glyph" aria-hidden />
				<span className="service-usage-value">{formatCount(u.nodes)}</span>
			</span>
			<span className="service-usage-metric service-usage-edges" aria-label={`엣지 ${u.edges}`}>
				<span className="service-usage-glyph" aria-hidden />
				<span className="service-usage-value">{formatCount(u.edges)}</span>
			</span>
		</span>
	)
}

/**
 * 서비스 카드/테이블 셀에서 MCP 엔드포인트 URL 을 보여주고 복사 버튼 제공.
 *
 * 카드 전체가 `<Link>` 로 감싸져 있어서 복사 버튼 클릭이 Link 네비를 트리거하지 않도록
 * `stopPropagation` + `preventDefault` 를 건다. 접근성상 button 태그를 중첩해도 HTML 유효.
 */
function McpEndpointRow(props: { readonly baseUrl: string; readonly slug: string }) {
	const url = `${props.baseUrl}/mcp/${props.slug}`
	const [copied, setCopied] = useState(false)
	function onCopy(e: React.MouseEvent) {
		e.preventDefault()
		e.stopPropagation()
		void navigator.clipboard?.writeText(url).then(() => {
			setCopied(true)
			window.setTimeout(() => setCopied(false), 1400)
		})
	}
	return (
		<div className="service-card-mcp" title={url}>
			<span className="service-card-mcp-label">MCP</span>
			<code className="service-card-mcp-url">/mcp/{props.slug}</code>
			<button
				type="button"
				className="service-card-mcp-copy"
				onClick={onCopy}
				aria-label="MCP 엔드포인트 URL 복사"
			>
				{copied ? "복사됨" : "복사"}
			</button>
		</div>
	)
}

/**
 * 팩 칩 — hover/focus 시 pack 의 description + blockIds 개수 를 Hover Card 로 노출.
 *
 * `<Link>` 내부에 있어서 button 중첩 불가 — span + CSS :hover 로만 동작.
 * 키보드 접근은 카드 전체 Link 포커스에서 활용하며, 팩 세부 정보는 마우스 hover 전용.
 */
/**
 * 호버 카드를 `document.body` Portal 로 띄우기 위한 trigger 좌표 계산 훅.
 * 테이블 뷰는 `overflow-x: auto` + 상위 ancestor 의 `transform`/`contain` 으로
 * `position: absolute`/`fixed` 카드가 잘리므로, trigger rect 를 viewport 기준으로 읽어
 * body 에 Portal 렌더하고 `position: fixed` + inline top/left 로 배치한다.
 *
 * 카드는 `pointer-events: none` 으로 마우스 이벤트가 trigger 로 관통 → 카드 위에 커서가
 * 지나가도 trigger mouseleave 가 발생하지 않아 깜박임이 없다.
 */
function useHoverCardAnchor() {
	const triggerRef = useRef<HTMLSpanElement>(null)
	const [anchor, setAnchor] = useState<DOMRect | null>(null)
	const show = useCallback(() => {
		const r = triggerRef.current?.getBoundingClientRect()
		if (r) setAnchor(r)
	}, [])
	const hide = useCallback(() => setAnchor(null), [])
	return { triggerRef, anchor, show, hide }
}

/** 카드가 trigger 위로 떴을 때 viewport 상단에 붙어버리지 않도록 flip 판정에 쓰이는 대략적 카드 높이. */
const HOVER_CARD_FLIP_THRESHOLD = 220

function hoverCardStyle(anchor: DOMRect): React.CSSProperties {
	const flipBelow = anchor.top < HOVER_CARD_FLIP_THRESHOLD
	return {
		position: "fixed",
		top: flipBelow ? anchor.bottom + 8 : anchor.top - 8,
		left: anchor.left,
		bottom: "auto",
		opacity: 1,
		transform: flipBelow ? "none" : "translateY(-100%)",
		pointerEvents: "none"
	}
}

function PackChip(props: { readonly id: string; readonly pack: OntologyPack | undefined }) {
	const { id, pack } = props
	const { triggerRef, anchor, show, hide } = useHoverCardAnchor()
	return (
		<span
			ref={triggerRef}
			className="chip service-card-pack-chip is-hoverable"
			onMouseEnter={show}
			onMouseLeave={hide}
		>
			{pack ? (
				<>
					<span aria-hidden style={{ marginRight: 4 }}>
						{pack.icon}
					</span>
					{pack.displayName}
				</>
			) : (
				id
			)}
			{pack &&
				anchor &&
				createPortal(
					<span className="pack-hover-card" role="tooltip" style={hoverCardStyle(anchor)}>
						<span className="pack-hover-card-head">
							<span aria-hidden>{pack.icon}</span>
							<strong>{pack.displayName}</strong>
						</span>
						<code className="inline pack-hover-card-id">{pack.id}</code>
						<span className="pack-hover-card-desc">{pack.description}</span>
						<span className="pack-hover-card-meta">
							{pack.blockIds.length} blocks · {pack.slice.kinds.length} kinds · {pack.slice.relations.length}{" "}
							relations
						</span>
					</span>,
					document.body
				)}
		</span>
	)
}

/**
 * 팩 overflow 칩 — 넘쳐난 팩들을 "+N" 으로 표시하고 hover 시 전체 이름 리스트를 보여준다.
 */
function PackOverflowChip(props: {
	readonly count: number
	readonly ids: ReadonlyArray<string>
	readonly packById: ReadonlyMap<string, OntologyPack>
}) {
	const { count, ids, packById } = props
	const { triggerRef, anchor, show, hide } = useHoverCardAnchor()
	return (
		<span
			ref={triggerRef}
			className="chip service-card-pack-chip service-card-pack-overflow is-hoverable"
			aria-label={`추가 팩 ${count}개`}
			onMouseEnter={show}
			onMouseLeave={hide}
		>
			+{count}
			{anchor &&
				createPortal(
					<span
						className="pack-hover-card pack-hover-card-list"
						role="tooltip"
						style={hoverCardStyle(anchor)}
					>
						<span className="pack-hover-card-head">
							<strong>추가 팩 {count}개</strong>
						</span>
						<ul>
							{ids.map((id) => {
								const pack = packById.get(id)
								return (
									<li key={id}>
										<span aria-hidden>{pack?.icon ?? "◦"}</span>
										{pack?.displayName ?? id}
									</li>
								)
							})}
						</ul>
					</span>,
					document.body
				)}
		</span>
	)
}

/**
 * 테이블 뷰 — 카드 그리드와 동일 데이터를 정렬 가능 컬럼으로 표시.
 * 각 행 클릭 시 `/services/:slug` 로 navigate. 복사 버튼 등 행 내부 인터랙션은
 * `e.stopPropagation()` 으로 행 클릭 네비를 차단한다.
 */
function ServiceTableView(props: {
	readonly services: ReadonlyArray<ServiceSummary>
	readonly packById: ReadonlyMap<string, OntologyPack>
	readonly blockCountFor: (s: ServiceSummary) => number
	readonly matchedPresetFor: (s: ServiceSummary) => OntologyPreset | undefined
	readonly baseUrl: string
	readonly usageFor: (s: ServiceSummary) => UsageState | undefined
}) {
	const { services, packById, blockCountFor, matchedPresetFor, baseUrl, usageFor } = props
	const navigate = useNavigate()
	return (
		<div className="services-page-table-wrap">
			<table className="services-page-table">
				<thead>
					<tr>
						<th>서비스</th>
						<th>상태</th>
						<th>블럭</th>
						<th>규모</th>
						<th>팩</th>
						<th>MCP</th>
						<th>오너</th>
						<th>생성</th>
					</tr>
				</thead>
				<tbody>
					{services.map((s) => {
						const blockCount = blockCountFor(s)
						const matchedPreset = matchedPresetFor(s)
						const packIds = s.ontologySlice.packIds ?? []
						const starterLabel = s.ontologySlice.presetId
							? (matchedPreset?.displayName ?? s.ontologySlice.presetId)
							: matchedPreset?.displayName
						return (
							<tr
								key={s.serviceSlug}
								className="services-page-table-row"
								onClick={() => navigate(`/services/${s.serviceSlug}`)}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault()
										navigate(`/services/${s.serviceSlug}`)
									}
								}}
								tabIndex={0}
							>
								<td>
									<div className="services-page-table-name">
										<strong>{s.displayName}</strong>
										<code className="inline">{s.serviceSlug}</code>
										{starterLabel && (
											<span className="badge service-card-starter" title="스타터 프리셋과 일치">
												{starterLabel}
											</span>
										)}
									</div>
								</td>
								<td>
									<span className={s.status === "active" ? "badge badge-active" : "badge badge-disabled"}>
										{s.status}
									</span>
								</td>
								<td>
									{blockCount > 0 ? (
										<span className="service-card-block-count">{blockCount}</span>
									) : (
										<span className="service-card-slice-warn">미설정</span>
									)}
								</td>
								<td>
									<UsageCell usage={usageFor(s)} />
								</td>
								<td>
									<div className="services-page-table-packs">
										{packIds.slice(0, 3).map((id) => (
											<PackChip key={id} id={id} pack={packById.get(id)} />
										))}
										{packIds.length > 3 && (
											<PackOverflowChip
												count={packIds.length - 3}
												ids={packIds.slice(3)}
												packById={packById}
											/>
										)}
										{packIds.length === 0 && <span className="muted">—</span>}
									</div>
								</td>
								<td>
									<McpEndpointRow baseUrl={baseUrl} slug={s.serviceSlug} />
								</td>
								<td className="muted">{s.ownerEmail}</td>
								<td className="muted">{s.createdAt.slice(0, 10)}</td>
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}

/**
 * 서비스 등록 위저드 — 단일 단계 모달. 기존 3-열 인라인 폼보다 안내·검증이 풍부하고 curl 예시를 제공한다.
 */
function RegistrationWizard(props: {
	readonly baseUrl: string
	readonly onClose: () => void
	readonly onRegistered: () => Promise<void>
	readonly register: (input: {
		readonly serviceSlug: string
		readonly displayName: string
		readonly ownerEmail: string
	}) => Promise<void>
}) {
	const [form, setForm] = useState({ serviceSlug: "", displayName: "", ownerEmail: "" })
	const [busy, setBusy] = useState(false)
	const [err, setErr] = useState<string | undefined>()

	const slugValid = /^[a-z][a-z0-9-]{1,47}$/.test(form.serviceSlug)
	const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.ownerEmail)
	const canSubmit = slugValid && !!form.displayName && emailValid && !busy

	async function submit() {
		if (!canSubmit) return
		setBusy(true)
		setErr(undefined)
		try {
			await props.register(form)
			await props.onRegistered()
			props.onClose()
		} catch (e) {
			setErr((e as Error).message)
		} finally {
			setBusy(false)
		}
	}

	return (
		<dialog
			className="modal-backdrop"
			open
			onMouseDown={(e) => e.currentTarget === e.target && props.onClose()}
		>
			<form
				className="modal"
				onMouseDown={(e) => e.stopPropagation()}
				onSubmit={(e) => {
					e.preventDefault()
					void submit()
				}}
			>
				<div className="modal-head">
					<h2>새 서비스 등록</h2>
					<p>
						완료 시 <code className="inline">{props.baseUrl}/mcp/&#123;slug&#125;</code> MCP 엔드포인트가
						활성화됩니다.
					</p>
				</div>

				<div className="modal-body">
					{err && (
						<div className="alert alert-danger" style={{ marginBottom: 14 }}>
							{err}
						</div>
					)}

					<div className="field">
						<label className="field-label" htmlFor="slug">
							서비스 슬러그
						</label>
						<input
							id="slug"
							className="input input-mono"
							placeholder="patmat"
							value={form.serviceSlug}
							onChange={(e) =>
								setForm({ ...form, serviceSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })
							}
							spellCheck={false}
						/>
						<span className="field-hint">
							kebab-case · 소문자+숫자+하이픈 · 2–48자. 이 값이 MCP 라우트 경로가 됩니다.
						</span>
					</div>

					<div className="field">
						<label className="field-label" htmlFor="name">
							표시 이름
						</label>
						<input
							id="name"
							className="input"
							placeholder="대시보드 표시 이름"
							value={form.displayName}
							onChange={(e) => setForm({ ...form, displayName: e.target.value })}
						/>
					</div>

					<div className="field">
						<label className="field-label" htmlFor="email">
							오너 이메일
						</label>
						<input
							id="email"
							className="input"
							type="email"
							placeholder="owner@example.com"
							value={form.ownerEmail}
							onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
						/>
						<span className="field-hint">API 키 만료 · 장애 알림 수신처.</span>
					</div>
				</div>

				<div className="modal-foot">
					<button type="button" className="btn btn-ghost" onClick={props.onClose}>
						취소
					</button>
					<button type="submit" className="btn btn-primary" disabled={!canSubmit}>
						{busy ? "등록 중…" : "등록하기"}
					</button>
				</div>
			</form>
		</dialog>
	)
}
